import React, { HTMLAttributes } from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";
import CustomEvent from "core/customEvent/index";
import Input from "core/input/";
import Popover from "react-popover";
import SVGIcons from "assets/icon";
import "./SVGIconLabelButton.styl";
import Helper from "core/helper/index";
import pubsub from "core/global/global";
/**
 * @description SVGIconLabelButton UI Component Properties
 * @todo 이 버튼의 타입은 아래 Property들을 조합하여 결정된다
 * -> 기능 파악 어려움. -> 가독성/이해 좋지 않음.
 * 버튼 타입은 'type'이라는 변수를 이용해 이미 정해진 타입을 사용하도록 고정하고,
 * 변수이름은 수정필요.
 * @interface ISVGIconLabelButtonProps
 * @extends {HTMLAttributes<HTMLDivElement>}
 */
interface ISVGIconLabelButtonProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * @description 버튼 ID
   */
  identifier: string;
  alone?: boolean;
  active?: boolean;
  /**
   * @description 버튼 레이블
   */
  label: string;
  /**
   * @description 버튼 사용 가능 여부 지정
   */
  disabled?: boolean;
  /**
   * @description 버튼 툴팁
   */
  tooltip?: string;
  /**
   * @description 버튼 아이콘(현재 SVG만 지원)
   */
  svgIcon?: any;
  svgActiveIcon?: any;
  svgComponentIcon?: any;
  /**
   * @description 하위 Elements
   */
  children?: NodeList;
  /**
   * @description 확장 UI 기능을 가진다면 true, 없으면 false
   */
  hasExpansion: boolean;
  /**
   * @description 확장 UI가 열린상태로 렌더링한다면 true, 그렇지 않으면 false
   */
  isExtensionOpen?: boolean;
  /**
   * @description 버튼 영역 어디나 클릭했을때, 확장 UI를 실행하면 true, 그렇지 않다면 false
   */
  isExpandedOnButtonClick?: boolean;
  isToggleButton?: boolean;
  /**
   * @description 확장 UI Element
   */
  extensionElement?: any;
  /**
   * @description 확장 UI 위치
   */
  extensionPlace?: string;
  /**
   * @description 버튼 Click 이벤트 핸들러
   */
  onButtonClick?: ((event: Event, isActive: boolean) => void) | null;
  /**
   * @description 아이콘 Click 이벤트 핸들러
   */
  onSvgIconClick?: ((event: Event) => void) | null;
  /**
   * @description 확장 버튼 Click 이벤트 핸들러
   */
  onExpansionButtonClick?: ((event: Event) => void) | null;
}

/**
 * @description SVGIconLabelButton UI Component Properties
 *
 * @interface ISVGIconLabelButtonStates
 * @extends {HTMLAttributes<HTMLDivElement>}
 */
interface ISVGIconLabelButtonStates extends HTMLAttributes<HTMLDivElement> {
  /**
   * @description 현재 확장 UI 열림/닫힘 상태
   */
  isExtensionOpen: boolean | undefined;
  isActiveState: boolean | undefined;
  isToggleButton: boolean | undefined;
}

/**
 * @description SVG 아이콘과 레이블을 출력하는 버튼 UI Component
 * 필요에 따라 Dropdown 또는 Popover UI를 연동할 수 있음
 * (보다 효율적인 구현방법으로 추후 변경 가능)
 *
 * @class SVGIconLabelButton
 * @extends {React.Component<ISVGIconLabelButtonProps, ISVGIconLabelButtonStates>}
 */
class SVGIconLabelButton extends React.Component<
  ISVGIconLabelButtonProps,
  ISVGIconLabelButtonStates
> {
  static propTypes: PropTypes.InferProps<ISVGIconLabelButtonProps>;

  /**
   * Default 프로퍼티 값 정의
   *
   * @static defaultProps
   * @memberof SVGIconLabelButton
   */
  static defaultProps = {
    id: "",
    label: "",
    alone: false,
    disabled: false,
    tooltip: "",
    svgIcon: "",
    children: [],
    active: false,
    svgComponentIcon: null,
    svgActiveIcon: null,
    hasExpansion: false,
    isExtensionOpen: false,
    isExpandedOnButtonClick: false,
    isToggleButton: false,
    extensionElement: null,
    extensionPlace: "below",
    onButtonClick: null,
    onSvgIconClick: null,
    onExpansionButtonClick: null
  };

  private tokenToggleActiveHandler = null;
  private tokenSetActiveHandler = null;

  /**
   * @description Creates an instance of SVGIconLabelButton.
   * state 디폴트 값 정의
   * @param {ISVGIconLabelButtonProps} props
   * @memberof SVGIconLabelButton
   */
  constructor(props: ISVGIconLabelButtonProps) {
    super(props);
    this.state = {
      isExtensionOpen: props.isExtensionOpen,
      isToggleButton: props.isToggleButton,
      //isActiveState: false
      isActiveState: props.active
    };
  }
  /**
   * @method componentDidMount
   * @description 컴포넌트와 관련된 이벤트 핸들러 등록
   *
   * @memberof SVGIconLabelButton
   */
  componentDidMount() {
    if(this.props.id === "text"){
      this.setState({ isActiveState: true });
    }
    const element = ReactDOM.findDOMNode(this);
    if (element !== undefined && element !== null) {
      element.addEventListener("click", this.handleClick);
      element.addEventListener("mouseup", this.handleMouseUp);
      element.addEventListener("mousedown", this.handleMousedown);
      //element.addEventListener("mouseleave", this.handleMouseout);
    }

    this.tokenToggleActiveHandler = pubsub.subscribe(
      CustomEvent.Messages.TOGGLE_ACTIVE_TOOLBAR_BUTTON,
      this.handleToggleActiveToolbarButton
    );

    this.tokenSetActiveHandler = pubsub.subscribe(
      CustomEvent.Messages.SET_ACTIVE_TOOLBAR_BUTTON,
      this.handleSetActiveToolbarButton
    );

    pubsub.subscribe("CLOSE_SUB_ELEMENT", (payload: any) => {
      const { extensionElement } = this.props;
      const { element, name, isActive, noSideEffect, justClose } = payload;

      if (justClose) {
        if (extensionElement) {
          //if (extensionElement.type.name === element.name) {
          if (this.props.id === name) {
            this.setState({ isActiveState: isActive, isExtensionOpen: false });
          }
        }
        return;
      }

      if (noSideEffect) {
        if (extensionElement && extensionElement.type.name === element.name) {
          this.setState({ isActiveState: isActive, isExtensionOpen: false });
        }
        return;
      }

      this.setState({
        isActiveState: false
      });

      if (extensionElement) {
        //if (extensionElement.type.name === element.name) {
        if (this.props.id === name) {
          this.setState({ isActiveState: isActive, isExtensionOpen: false });
        }
      }
    });
  }

  /**
   * @method componentWillUnmount
   * @description 컴포넌트와 관련된 이벤트 핸들러 해제
   *
   * @memberof SVGIconLabelButton
   */
  componentWillUnmount() {
    const element = ReactDOM.findDOMNode(this);
    if (element !== undefined && element !== null) {
      element.removeEventListener("click", this.handleClick);
      element.removeEventListener("mousedown", this.handleMousedown);
      element.removeEventListener("mouseup", this.handleMouseUp);
      element.removeEventListener("mouseleave", this.handleMouseout);
      (pubsub as any).unsubscribe(this.tokenToggleActiveHandler);
      (pubsub as any).unsubscribe(this.tokenSetActiveHandler);
    }
  }

  /**
   * @method getImageType
   * @description 아이콘 이미지 타입 반환.
   * (추후, svg 이외에 다른 아이콘 이미지 타입 지원을 위해 수정 필요)
   * @memberof SVGIconLabelButton
   */
  getImageType() {
    const { svgIcon, svgComponentIcon } = this.props;
    if (svgComponentIcon) {
      return "inline";
    }
    if (svgIcon !== "") {
      return "svg";
    }

    return "none";
  }

  getButtonStateClass = () => {
    const { disabled } = this.props;
    const { isActiveState } = this.state;
    if (isActiveState && !disabled) {
      return "active";
    }
    return "";
  };

  handleMouseUp(e: any) {
    const element = ReactDOM.findDOMNode(this);
    if (element) {
      element.removeEventListener("mouseleave", this.handleMouseout);
    }
  }

  /**
   *
   * @todos 지금은 PubSub.js를 이용해 구현했는데,
   * 추후 RadioButtonGroup을 만들어서 그룹내에 있는 버튼은 토글되도록 수정해야함
   * @memberof SVGIconLabelButton
   */
  handleToggleActiveToolbarButton = (id: any) => {
    if (id === this.props.id) {
      /// 2019.06.20 button toggle 기능 오작동 버그 수정
      //this.setState({ isActiveState: true });
      this.setState({ isActiveState: !this.state.isActiveState });
    } else {
      !this.props.alone && this.setState({ isActiveState: false });
    }
  };
  handleSetActiveToolbarButton = (payload: any, msg: any) => {
    const { id, active } = payload;

    if (id === this.props.id) {
      /// 2019.06.20 button toggle 기능 오작동 버그 수정
      //this.setState({ isActiveState: true });
      this.setState({ isActiveState: active });
    } else {
      //this.setState({ isActiveState: !active });
      !this.props.alone && this.setState({ isActiveState: false });
    }
  };

  handleMousedown = () => {
    const { disabled } = this.props;
    const { isToggleButton } = this.state;
    const element = ReactDOM.findDOMNode(this);
    if (element) {
      element.addEventListener("mouseleave", this.handleMouseout);
    }
    if (!isToggleButton) {
      return;
    }
    // const { isActiveState } = this.state;
    // this.setState({ isActiveState: !isActiveState });

    if (disabled === true) {
      return;
    }

    (pubsub as any).publish(
      CustomEvent.Messages.TOGGLE_ACTIVE_TOOLBAR_BUTTON,
      this.props.id
    );
  };

  handleMouseout = (event: any) => {
    //const { onButtonClick, hasExpansion } = this.props;
    const {
      onButtonClick,
      hasExpansion,
      isToggleButton,
      disabled
    } = this.props;
    const element = ReactDOM.findDOMNode(this);
    if (element) {
      element.removeEventListener("mouseleave", this.handleMouseout);
    }

    if (disabled === true) {
      return;
    }

    //if (event.which === 1 && this.state.isActiveState) {
    if (Input.Mouse.NormalizeMouseEvent.buttons(event) === 1) {
      //if (onButtonClick && this.state.isActiveState) {
      if (onButtonClick && isToggleButton) {
        onButtonClick(event, false);
      }
      this.setState({ isActiveState: false });
      if (hasExpansion) {
        this.setState({
          isExtensionOpen: false
        });
      }
    }
  };

  /**
   * @method handleClick
   * @description 버튼 클릭시 발생하는 이벤트를 처리하는 핸들러
   * 이벤트 발생시 사용자가 입력한 Callback함수를 실행하고,
   * isExpandedOnButtonClick 옵션이 켜진경우, 확장 UI 열기/닫기를 토글한다.
   * @memberof SVGIconLabelButton
   */
  handleClick = (event: any) => {
    const {
      onButtonClick,
      isToggleButton,
      hasExpansion,
      isExpandedOnButtonClick,
      disabled
    } = this.props;

    let isActive = this.state.isActiveState;

    if (isActive === undefined || isActive === null) {
      isActive = false;
    }

    if (disabled === true) {
      return;
    }

    if (!isToggleButton && !hasExpansion) {
      this.setState({
        isActiveState: false
      });
    } else if (isExpandedOnButtonClick) {
      if (this.state.isActiveState && !this.state.isExtensionOpen) {
        this.setState({ isExtensionOpen: true });
        isActive = true;
      } else {
        this.TogglePopoverExtension();
        isActive = this.state.isActiveState;
      }
    }

    if (onButtonClick) {
      onButtonClick(event, isActive as any);
    }
  };

  /**
   * @method handleSVGIconClick
   * @description SVGIcon에 클릭 이벤트가 발생하면, 사용자가 입력한 Callback 함수를 실행
   * @memberof SVGIconLabelButton
   */
  handleSVGIconClick = (event: any) => {
    const { onSvgIconClick, disabled } = this.props;
    if (disabled === true) {
      return;
    }

    ///
    /// 1. SVGIconClick 핸들러를 Property로 받았다면, 호출
    ///
    if (onSvgIconClick !== undefined && onSvgIconClick !== null) {
      onSvgIconClick(event);
    }

    /// 테스트 필요 코드
    ///
    /// 2. 만약, 확장 메뉴가 열려있다면, 닫는다.
    ///
    if (!this.state.isActiveState && this.state.isExtensionOpen) {
      this.setState({ isExtensionOpen: false });
    }
  };

  /**
   * @method TogglePopoverExtension
   * @description SVGIcon에 클릭 이벤트가 발생하면, 사용자가 입력한 Callback 함수를 실행
   * @memberof SVGIconLabelButton
   */
  TogglePopoverExtension = () => {
    const isExtensionOpen = !this.state.isExtensionOpen;
    let isActiveState = isExtensionOpen;
    this.setState({
      isExtensionOpen: isExtensionOpen,
      isActiveState: isActiveState
    });
  };

  /**
   * @method actionExtensionUIOnOuter
   * @description 확장 UI 밖에서 클릭이 발생할경우, 처리 함수
   * 현재 구현은, 확장 UI 밖에서 클릭되면 확장 UI를 닫는다
   * @memberof SVGIconLabelButton
   */
  actionExtensionUIOnOuter = () => {
    //this.TogglePopoverExtension();
    this.setState({
      isExtensionOpen: false
    });
  };

  /**
   * @method handlePopover
   * @description 확장 UI를 Popover
   * @memberof SVGIconLabelButton
   */
  handlePopover = () => {
    const {
      isExpandedOnButtonClick,
      isToggleButton,
      hasExpansion
    } = this.props;
    ///
    /// Toggle 버튼이 아니고, 확장 메뉴도 없으면 제목부분 클릭에 따른 특별한동작을 수행하지 않는다.
    ///
    if (!isToggleButton && !hasExpansion) {
      return;
    }
    ///
    /// Toggle 버튼이고, 확장이 없는 버튼
    ///
    if (isToggleButton && !hasExpansion) {
      // this.setState({
      //   isActiveState: !isActiveState
      // });
      return;
    }
    ///
    /// 확장 메뉴가 있고, 아이콘과 제목 동작이 다른경우
    /// 제목 클릭시 무조건 활성화 상태 유지하면서 확장 메뉴만 여닫음
    ///
    if (hasExpansion && !isExpandedOnButtonClick) {
      const isExtensionOpen = !this.state.isExtensionOpen;
      this.setState({
        isExtensionOpen: isExtensionOpen,
        isActiveState: true
      });
      return;
    }

    ///
    /// 버튼 자체를 클릭했을때, UI가 확장되도록 설정된 경우에는
    /// 확장 버튼 이벤트는 중복실행되는 것은 실행하지 않음
    ///
    if (!isExpandedOnButtonClick) {
      this.TogglePopoverExtension();
    }
  };

  /**
   * @method renderImage
   * @description SVG 아이콘 이미지를 렌더링
   * @returns SVG Element or no-image text
   * @memberof SVGIconLabelButton
   */
  renderImage() {
    let type = this.getImageType();
    let { isActiveState } = this.state;
    let { svgActiveIcon, disabled } = this.props;
    if (type === "svg") {
      return (
        <svg>
          <use xlinkHref={this.props.svgIcon} />
        </svg>
      );
    } else if (type === "inline") {
      if (isActiveState === true && svgActiveIcon && !disabled) {
        return <this.props.svgActiveIcon />;
      } else {
        return <this.props.svgComponentIcon />;
      }
    } else {
      return <noscript>no image</noscript>;
    }
  }

  /**
   * @method renderExtensionUIUIToggleButton
   * @description 확장 UI 토글 버튼 렌더링(거꾸로된 삼각형)
   * @returns SVG Element
   * @memberof SVGIconLabelButton
   */
  renderExtensionUIUIToggleButton() {
    return <SVGIcons.SVGDownTriangleIcon />;
  }

  /**
   * @method renderExtensionUI
   * @description 확장 UI 렌더링
   * @returns 확장 UI Element
   * @memberof SVGIconLabelButton
   */
  renderExtensionUI() {
    const { hasExpansion, extensionElement, disabled } = this.props;

    if (disabled === true) {
      return;
    }

    ///
    /// 확장 UI가 없는경우, 렌더링 진행하지 않음
    ///
    if (!hasExpansion) {
      return;
    }

    ///
    /// 옵션에 따라, 확장 UI 토글 버튼만 렌더링
    /// (추후, 적절한 방법으로 수정 필요)
    ///
    if (extensionElement === null || extensionElement === undefined) {
      if (hasExpansion) {
        return this.renderExtensionUIUIToggleButton();
      }

      return;
    }

    ///
    /// 확장 UI 렌더링
    ///
    return this.renderExtensionUIUIToggleButton();

    // return (
    //   <Popover place="below" {...popoverProps}>
    //     {this.renderExtensionUIUIToggleButton()}
    //   </Popover>
    // );
  }

  renderSVGLabelButton() {
    const { id: realId, label, tooltip } = this.props;
    return (
      <div
        id={realId}
        // identifier={id as any}
        className={"toolbarSectionButton " + this.getButtonStateClass()}
        title={tooltip}
      >
        <div className="svgContainer" onClick={this.handleSVGIconClick}>
          {this.renderImage()}
        </div>
        <div className="buttonLabel" onClick={this.handlePopover}>
          <span>{label} </span>
          <div className="svgContainer">{this.renderExtensionUI()}</div>
        </div>
      </div>
    );
  }

  /**
   * @method render
   * @description SVGIcon LabelButton UI 컴포넌트 렌더링
   * @returns 렌더링된 React Elements
   * @memberof SVGIconLabelButton
   */
  render() {
    const { id, hasExpansion, extensionElement } = this.props;
    const { isExtensionOpen } = this.state;
    ///
    /// 확장 UI 렌더링
    ///
    const popoverProps = {
      isOpen: isExtensionOpen,
      ///
      /// Issue :
      /// contextmenu위에서 왼쪽 마우스 버튼을 누른채 mouseleave 하고, 빠르게 버튼을 다시 누르면
      /// 렌더링이 안되는 문제가 발생 하여, 애니메이션 일단 제거
      enterExitTransitionDurationMs: 0,
      //offsetX: -40,
      //preferPlace: extensionPlace,
      preferPalce: "below",
      //place: "below",
      //onOuterAction: () => this.actionExtensionUIOnOuter,
      onOuterAction: (e: any) => {
        ///
        /// HACK! outerAction이랑 handleClick 이벤트가 같이 발생하는 문제 수정
        /// Element id를 이용하였는데, 이 방법은 사용자가 꼭 유일한 id 값을 입력해줘야하는
        /// 책임을 지운다. 사용자가 신경쓰지 않아도 되는 방법을 생각해보고 수정해야함.
        ///
        const { onButtonClick } = this.props;
        if (onButtonClick) {
          onButtonClick(e, false);
        }
        const closestElement = Helper.DomHelper.closest(e.srcElement, "#" + id);

        if (closestElement.length === 0) {
          this.setState({ isActiveState: false });
          this.actionExtensionUIOnOuter();
          return;
        }
      },
      tipSize: 0.01,
      body: [extensionElement]
    };

    ///
    /// 확장 UI가 없는경우, 렌더링 진행하지 않음
    ///
    if (
      !hasExpansion ||
      (extensionElement === null || extensionElement === undefined)
    ) {
      return <React.Fragment>{this.renderSVGLabelButton()}</React.Fragment>;
    }
    return (
      <Popover place="below" {...popoverProps}>
        {this.renderSVGLabelButton()}
      </Popover>
    );
  }
}

export default SVGIconLabelButton;
